import React from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { LatestClosedPeriod } from '../../../types/api';

const REPORT_MODES = [
  { value: 'pnl', label: 'P&L' },
  { value: 'costs', label: 'Kostnader' },
  { value: 'revenue', label: 'Intäkter' },
] as const;

const REPORT_LAYOUTS = [
  { value: 'standard', label: 'Standard', description: 'En kolumn för hela perioden' },
  { value: 'monthly', label: 'Månadsvis', description: 'En kolumn per månad i vald period' },
] as const;

const FISCAL_YEARS = ['2024', '2025', '2026', '2027', '2028', '2029', '2030', '2031', '2032', '2033', '2034'];

const toPeriodLabel = (period: LatestClosedPeriod) => {
  if (typeof period.label === 'string' && period.label.trim() !== '') {
    return period.label.trim();
  }
  return period.startDate.slice(0, 7);
};

type ReportSettingsSectionProps = {
  reportMode: 'costs' | 'revenue' | 'pnl';
  onReportModeChange: (_event: React.MouseEvent<HTMLElement>, nextMode: 'costs' | 'revenue' | 'pnl' | null) => void;
  reportLayout: 'standard' | 'monthly';
  onReportLayoutChange: (_event: React.MouseEvent<HTMLElement>, nextLayout: 'standard' | 'monthly' | null) => void;
  fiscalYearId: string;
  onFiscalYearChange: (value: string) => void;
  previousMonthStartLocal: string;
  previousMonthEndLocal: string;
  currentMonthStartLocal: string;
  todayLocal: string;
  yearToDateStartLocal: string;
  latestClosedPeriod: LatestClosedPeriod | null;
  latestClosedPeriodError: string | null;
  isGroupTarget: boolean;
  isPresetActive: (presetStartDate: string, presetEndDate: string) => boolean;
  applyDateRange: (nextStartDate: string, nextEndDate: string) => void;
  dateError: string | null;
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
};

const ReportSettingsSection: React.FC<ReportSettingsSectionProps> = ({
  reportMode,
  onReportModeChange,
  reportLayout,
  onReportLayoutChange,
  fiscalYearId,
  onFiscalYearChange,
  previousMonthStartLocal,
  previousMonthEndLocal,
  currentMonthStartLocal,
  todayLocal,
  yearToDateStartLocal,
  latestClosedPeriod,
  latestClosedPeriodError,
  isGroupTarget,
  isPresetActive,
  applyDateRange,
  dateError,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const theme = useTheme();

  const datePresets = [
    { label: 'Föregående månad', start: previousMonthStartLocal, end: previousMonthEndLocal },
    { label: 'Innevarande månad', start: currentMonthStartLocal, end: todayLocal },
    { label: 'År till idag', start: yearToDateStartLocal, end: todayLocal },
  ];
  const latestClosedPeriodActive = latestClosedPeriod
    ? isPresetActive(latestClosedPeriod.startDate, latestClosedPeriod.endDate)
    : false;
  const latestClosedPeriodChipLabel = latestClosedPeriod && !isGroupTarget
    ? `Senast stängda period: ${toPeriodLabel(latestClosedPeriod)}`
    : 'Senast stängda period';
  const isLatestClosedPeriodDisabled = isGroupTarget || !latestClosedPeriod;

  const reportTypeToggleSx = {
    px: 2,
    py: 1.4,
    textTransform: 'none',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.78 : 0.92),
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
    '& .MuiTypography-root': {
      color: 'inherit',
    },
    '& .MuiTypography-caption': {
      color: theme.palette.text.secondary,
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.04),
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.light, 0.18)
        : alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.text.primary,
      borderColor: `${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.5 : 0.24)} !important`,
      boxShadow: 'none',
    },
    '&.Mui-selected .MuiTypography-caption': {
      color: theme.palette.text.secondary,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.light, 0.2)
        : alpha(theme.palette.primary.main, 0.1),
    },
  };

  const sectionTitleSx = {
    mb: 1.75,
    fontWeight: 650,
    letterSpacing: '-0.01em',
    color: theme.palette.text.primary,
  };

  const softFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px !important',
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.78 : 0.95),
    },
  };

  const presetChipSx = (active: boolean, disabled = false) => ({
    borderRadius: '8px !important',
    fontWeight: 500,
    backgroundColor: active
      ? theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.light, 0.28)
        : alpha(theme.palette.primary.main, 0.1)
      : theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.02)
        : 'transparent',
    borderColor: active
      ? theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.light, 0.5)
        : alpha(theme.palette.primary.main, 0.24)
      : alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.24 : 0.5),
    color: active && theme.palette.mode === 'dark'
      ? theme.palette.common.white
      : theme.palette.text.primary,
    boxShadow: active && theme.palette.mode === 'dark'
      ? `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.2)}`
      : 'none',
    '&:hover': {
      backgroundColor: active
        ? theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.light, 0.32)
          : alpha(theme.palette.primary.main, 0.12)
        : alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.05 : 0.03),
    },
    '&.Mui-disabled': {
      opacity: 1,
      color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.45 : 0.5),
      borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.22 : 0.7),
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, disabled ? 0.035 : 0.02)
        : alpha(theme.palette.text.primary, disabled ? 0.035 : 0.02),
      boxShadow: 'none',
    },
  });

  return (
    <>
      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.background.paper, 0.98), boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Rapportinnehåll
        </Typography>

        <ToggleButtonGroup
          value={reportMode}
          exclusive
          onChange={onReportModeChange}
          fullWidth
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
            '& .MuiToggleButtonGroup-grouped': {
              m: 0,
              borderRadius: '12px !important',
              border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.22 : 0.46)} !important`,
            },
          }}
        >
          {REPORT_MODES.map((mode) => (
            <ToggleButton key={mode.value} value={mode.value} sx={reportTypeToggleSx}>
              <Stack spacing={0.5} sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {mode.label}
                </Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.background.paper, 0.98), boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Rapportlayout
        </Typography>

        <ToggleButtonGroup
          value={reportLayout}
          exclusive
          onChange={onReportLayoutChange}
          fullWidth
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.25,
            '& .MuiToggleButtonGroup-grouped': {
              m: 0,
              borderRadius: '12px !important',
              border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.22 : 0.46)} !important`,
            },
          }}
        >
          {REPORT_LAYOUTS.map((layout) => (
            <ToggleButton key={layout.value} value={layout.value} sx={reportTypeToggleSx}>
              <Stack spacing={0.5} sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {layout.label}
                </Typography>
                <Typography variant="caption">
                  {layout.description}
                </Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.background.paper, 0.98), boxShadow: theme.palette.mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.05)' }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Period
        </Typography>

        <Stack sx={{ width: '100%', alignItems: 'stretch' }}>
          <FormControl fullWidth size="small" sx={{ mb: 4 }}>
            <InputLabel>År</InputLabel>
            <Select
              value={fiscalYearId.split('-')[0]}
              onChange={(e) => onFiscalYearChange(String(e.target.value))}
              label="År"
              sx={softFieldSx}
            >
              {FISCAL_YEARS.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: '100%' }}>
              {datePresets.map((preset) => (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  clickable
                  variant="outlined"
                  sx={presetChipSx(isPresetActive(preset.start, preset.end))}
                  onClick={() => applyDateRange(preset.start, preset.end)}
                />
              ))}
              <Chip
                label={latestClosedPeriodChipLabel}
                clickable={!isLatestClosedPeriodDisabled}
                disabled={isLatestClosedPeriodDisabled}
                variant="outlined"
                sx={presetChipSx(latestClosedPeriodActive, isLatestClosedPeriodDisabled)}
                onClick={() => {
                  if (latestClosedPeriod && !isGroupTarget) {
                    applyDateRange(latestClosedPeriod.startDate, latestClosedPeriod.endDate);
                  }
                }}
              />
            </Stack>
          </Box>

          {dateError && (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
              {dateError}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
              width: '100%',
              alignItems: 'start',
              mb: 2,
            }}
          >
            <Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: theme.palette.text.secondary }}>
                  Start
                </Typography>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onStartDateChange(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!dateError}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    placeholder: 'YYYY-MM-DD',
                    min: '2000-01-01',
                    max: todayLocal,
                  }}
                  sx={{
                    ...softFieldSx,
                    '& input::-webkit-calendar-picker-indicator': {
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                    },
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: theme.palette.text.secondary }}>
                  Slut
                </Typography>
                <TextField
                  type="date"
                  value={endDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEndDateChange(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!dateError}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    placeholder: 'YYYY-MM-DD',
                    min: '2000-01-01',
                    max: todayLocal,
                  }}
                  sx={{
                    ...softFieldSx,
                    '& input::-webkit-calendar-picker-indicator': {
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </>
  );
};

export default React.memo(ReportSettingsSection);
